import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import {
    FaChartBar, FaServer, FaUserTie, FaSearch, FaSortUp, FaSortDown, FaSort, FaDownload,
} from "react-icons/fa";
import { descargarCsv } from "./csv";
import Spinner from "./Spinner";

const fmt = (v) =>
    v === null || v === undefined ? "—" : Number(v).toLocaleString("es-MX");

// "2026-06" -> "Jun 2026"
function mesLabel(key) {
    const [y, m] = key.split("-");
    const nombres = [
        "Ene", "Feb", "Mar", "Abr", "May", "Jun",
        "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
    ];
    return `${nombres[Number(m) - 1] || m} ${y}`;
}

// "2026-06" -> "Jun" (sin año, para los chips cuando el año va aparte)
function mesCorto(key) {
    const nombres = [
        "Ene", "Feb", "Mar", "Abr", "May", "Jun",
        "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
    ];
    return nombres[Number(key.split("-")[1]) - 1] || key;
}

// Columnas: key = campo en la fila, type = 'num' | 'text', ip = solo en scope IP.
const COLUMNS = [
    { key: "username", label: "Cuenta", type: "text" },
    { key: "remote_ip", label: "IP", type: "text", ip: true },
    { key: "cps_pico", label: "CPS pico", type: "num", right: true },
    { key: "cps_promedio_dia", label: "CPS prom/día", type: "num", right: true },
    { key: "simultaneas_pico", label: "Simult. pico", type: "num", right: true },
    { key: "simultaneas_promedio_dia", label: "Simult. prom/día", type: "num", right: true },
    { key: "total_llamadas", label: "Llamadas", type: "num", right: true },
    { key: "llamadas_contestadas", label: "Contestadas", type: "num", right: true },
    { key: "no_contestadas", label: "No contestadas", type: "num", right: true },
    { key: "dia_top", label: "Día pico", type: "text" },
];

export default function CpsAnalisis() {
    const navigate = useNavigate();
    const [scope, setScope] = useState("cuenta"); // 'cuenta' | 'ip'
    const [mesesDisp, setMesesDisp] = useState([]);
    const [meses, setMeses] = useState([]);
    const [anio, setAnio] = useState(null);
    const [rows, setRows] = useState([]);
    const [loadingMeses, setLoadingMeses] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [query, setQuery] = useState("");
    const [sortKey, setSortKey] = useState("cps_pico");
    const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'

    const toggleMes = (key) =>
        setMeses((prev) =>
            prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key]
        );

    // Años disponibles (de los meses con datos), mas reciente primero.
    const aniosDisp = useMemo(() => {
        const s = new Set(mesesDisp.map((m) => m.split("-")[0]));
        return Array.from(s).sort().reverse();
    }, [mesesDisp]);

    // Meses del año seleccionado, en orden de calendario (Ene -> Dic).
    const mesesDelAnio = useMemo(
        () => mesesDisp.filter((m) => anio && m.startsWith(anio + "-")).sort(),
        [mesesDisp, anio]
    );

    // Carga inicial: meses disponibles. Selecciona el mas reciente por defecto.
    useEffect(() => {
        let cancel = false;
        setLoadingMeses(true);
        axios
            .post(API_ROUTES.analisis.meses, {})
            .then((res) => {
                if (cancel) return;
                const lista = Array.isArray(res.data?.meses) ? res.data.meses : [];
                setMesesDisp(lista);
                setMeses(lista.length ? [lista[0]] : []);
                setAnio(lista.length ? lista[0].split("-")[0] : null);
            })
            .catch(() => {
                if (!cancel) setMesesDisp([]);
            })
            .finally(() => {
                if (!cancel) setLoadingMeses(false);
            });
        return () => {
            cancel = true;
        };
    }, []);

    // Carga de la tabla cuando cambian meses o scope.
    useEffect(() => {
        if (meses.length === 0) {
            setRows([]);
            return;
        }
        let cancel = false;
        setLoading(true);
        setError(null);
        axios
            .post(API_ROUTES.analisis.cps, { meses, scope })
            .then((res) => {
                if (cancel) return;
                const arr = Array.isArray(res.data?.data) ? res.data.data : [];
                setRows(
                    arr.map((r) => ({
                        ...r,
                        no_contestadas:
                            Number(r.total_llamadas || 0) - Number(r.llamadas_contestadas || 0),
                    }))
                );
            })
            .catch((err) => {
                if (cancel) return;
                setError(
                    err?.response?.data?.message || err?.message || "Error al cargar"
                );
                setRows([]);
            })
            .finally(() => {
                if (!cancel) setLoading(false);
            });
        return () => {
            cancel = true;
        };
    }, [scope, meses]);

    // Al cambiar de scope, si el orden era por una columna que ya no existe, reinicia.
    useEffect(() => {
        const valida = COLUMNS.some(
            (c) => c.key === sortKey && (!c.ip || scope === "ip")
        );
        if (!valida) {
            setSortKey("cps_pico");
            setSortDir("desc");
        }
    }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

    const onSort = (key) => {
        if (key === sortKey) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("desc"); // las metricas suelen verse mejor de mayor a menor
        }
    };

    // Filtro (buscador) + orden, en cliente.
    const visibles = useMemo(() => {
        const q = query.trim().toLowerCase();
        let out = rows;
        if (q) {
            out = rows.filter((r) =>
                [r.i_account, r.username, r.remote_ip]
                    .map((v) => (v == null ? "" : String(v).toLowerCase()))
                    .some((s) => s.includes(q))
            );
        }
        const col = COLUMNS.find((c) => c.key === sortKey);
        if (col) {
            const f = sortDir === "asc" ? 1 : -1;
            out = [...out].sort((a, b) => {
                const va = a[sortKey];
                const vb = b[sortKey];
                if (col.type === "num") {
                    return (Number(va || 0) - Number(vb || 0)) * f;
                }
                const sa = (va == null ? "" : String(va)).toLowerCase();
                const sb = (vb == null ? "" : String(vb)).toLowerCase();
                return (sa < sb ? -1 : sa > sb ? 1 : 0) * f;
            });
        }
        return out;
    }, [rows, query, sortKey, sortDir]);

    const totales = useMemo(() => {
        const llamadas = visibles.reduce((a, r) => a + Number(r.total_llamadas || 0), 0);
        const contestadas = visibles.reduce(
            (a, r) => a + Number(r.llamadas_contestadas || 0), 0
        );
        return { llamadas, contestadas, noContestadas: llamadas - contestadas };
    }, [visibles]);

    const cols = COLUMNS.filter((c) => !c.ip || scope === "ip");

    // Exporta exactamente lo que se ve (columnas del scope, filtro y orden actuales).
    const exportar = () => {
        const columnas = cols.map((c) => {
            if (c.key === "dia_top") {
                return {
                    key: "dia_top",
                    label: "dia_pico",
                    format: (v, r) =>
                        v ? `${String(v).slice(0, 10)} (${r.dia_top_llamadas ?? ""})` : "",
                };
            }
            if (c.key === "no_contestadas") {
                return {
                    key: "no_contestadas",
                    label: "no_contestadas",
                    format: (v, r) =>
                        v ?? Number(r.total_llamadas || 0) - Number(r.llamadas_contestadas || 0),
                };
            }
            return { key: c.key, label: c.label };
        });
        const sufijo = scope === "ip" ? "ip" : "cuenta";
        descargarCsv(
            columnas,
            visibles,
            `cps_resumen_${sufijo}_${meses.slice().sort().join("_") || "periodo"}`
        );
    };

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            {/* Encabezado */}
            <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <FaChartBar className="text-cyan-600" />
                        Carga de plataforma — CPS y sesiones
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Pico de llamadas por segundo y de sesiones concurrentes, por cuenta o
                        por IP de origen.
                    </p>
                </div>
                {visibles.length > 0 && !loading && (
                    <button
                        onClick={exportar}
                        className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-700"
                    >
                        <FaDownload /> Descargar CSV
                    </button>
                )}
            </div>

            {/* Controles */}
            <div className="flex flex-wrap items-start gap-4 mb-2">
                <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-white">
                    <ScopeBtn active={scope === "cuenta"} onClick={() => setScope("cuenta")}
                        icon={<FaUserTie />} label="Por cuenta" />
                    <ScopeBtn active={scope === "ip"} onClick={() => setScope("ip")}
                        icon={<FaServer />} label="Por IP" />
                </div>

                {loadingMeses ? (
                    <span className="text-sm text-slate-400 px-2 py-1.5">Cargando meses…</span>
                ) : mesesDisp.length === 0 ? (
                    <span className="text-sm text-slate-400 px-2 py-1.5">
                        Sin meses con datos todavía.
                    </span>
                ) : (
                    <div className="flex flex-col gap-2">
                        {/* Año */}
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-xs text-slate-400 mr-1">Año</span>
                            {aniosDisp.map((y) => (
                                <button
                                    key={y}
                                    onClick={() => setAnio(y)}
                                    className={`px-2.5 py-1 text-sm rounded-md transition ${anio === y
                                            ? "bg-cyan-600 text-white"
                                            : "bg-white border border-slate-200 text-slate-500 hover:border-slate-300"
                                        }`}
                                >
                                    {y}
                                </button>
                            ))}
                        </div>
                        {/* Meses del año seleccionado */}
                        <div className="flex flex-wrap items-center gap-1.5">
                            {mesesDelAnio.map((key) => {
                                const on = meses.includes(key);
                                return (
                                    <button
                                        key={key}
                                        onClick={() => toggleMes(key)}
                                        className={`px-2.5 py-1 text-sm rounded-md border transition ${on
                                                ? "bg-cyan-50 border-cyan-400 text-cyan-700"
                                                : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                                            }`}
                                    >
                                        {mesCorto(key)}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() =>
                                    setMeses((prev) => Array.from(new Set([...prev, ...mesesDelAnio])))
                                }
                                className="text-xs text-cyan-600 hover:text-cyan-700 px-1"
                            >
                                Año completo
                            </button>
                            {meses.length > 0 && (
                                <button
                                    onClick={() => setMeses([])}
                                    className="text-xs text-slate-400 hover:text-slate-600 px-1"
                                >
                                    Limpiar ({meses.length})
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Meses seleccionados (claro aunque crucen años) */}
            {meses.length > 0 && (
                <div className="text-xs text-slate-400 mb-4">
                    Seleccionados: {meses.slice().sort().map(mesLabel).join(", ")}
                </div>
            )}

            {/* Buscador */}
            <div className="mb-4 relative max-w-sm">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar cuenta, IP…"
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 outline-none"
                />
            </div>

            {/* Resumen neutral */}
            <div className="grid grid-cols-3 gap-3 mb-5">
                <Kpi label="Llamadas" value={totales.llamadas} />
                <Kpi label="Contestadas" value={totales.contestadas} />
                <Kpi label="No contestadas" value={totales.noContestadas} />
            </div>

            {error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-700 text-sm">
                    {error}
                </div>
            )}

            {/* Tabla */}
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-slate-50 text-slate-500 text-left">
                            {cols.map((c) => (
                                <SortableTh
                                    key={c.key}
                                    label={c.label}
                                    right={c.right}
                                    active={sortKey === c.key}
                                    dir={sortDir}
                                    onClick={() => onSort(c.key)}
                                />
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={cols.length}>
                                    <Spinner />
                                </td>
                            </tr>
                        ) : visibles.length === 0 ? (
                            <tr>
                                <td colSpan={cols.length} className="text-center py-10 text-slate-400">
                                    {meses.length === 0
                                        ? "Selecciona al menos un mes."
                                        : query
                                            ? "Sin resultados para la búsqueda."
                                            : "Sin datos para los meses seleccionados."}
                                </td>
                            </tr>
                        ) : (
                            visibles.map((r, i) => (
                                <tr
                                    key={`${r.i_account}-${r.remote_ip || ""}-${i}`}
                                    onClick={() =>
                                        navigate(
                                            `/analisis/cps/${r.i_account}?meses=${meses.join(",")}` +
                                            (scope === "ip" && r.remote_ip
                                                ? `&ip=${encodeURIComponent(r.remote_ip)}`
                                                : "")
                                        )
                                    }
                                    className="border-t border-slate-100 hover:bg-cyan-50/50 cursor-pointer"
                                    title="Ver detalle por dia"
                                >
                                    <Td>{r.username || "—"}</Td>
                                    {scope === "ip" && <Td mono>{r.remote_ip || "(sin IP)"}</Td>}
                                    <Td right>{fmt(r.cps_pico)}</Td>
                                    <Td right>{fmt(r.cps_promedio_dia)}</Td>
                                    <Td right>{fmt(r.simultaneas_pico)}</Td>
                                    <Td right>{fmt(r.simultaneas_promedio_dia)}</Td>
                                    <Td right>{fmt(r.total_llamadas)}</Td>
                                    <Td right>{fmt(r.llamadas_contestadas)}</Td>
                                    <Td right>{fmt(r.no_contestadas)}</Td>
                                    <Td>
                                        {r.dia_top
                                            ? `${String(r.dia_top).slice(0, 10)} (${fmt(r.dia_top_llamadas)})`
                                            : "—"}
                                    </Td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <p className="text-xs text-slate-400 mt-3">
                El pico es el peor segundo del periodo; el promedio/día es el pico de un
                día típico. En el mes en curso los picos son acumulados “hasta hoy”.
            </p>
        </div>
    );
}

function ScopeBtn({ active, onClick, icon, label }) {
    return (
        <button
            onClick={onClick}
            className={`px-4 py-1.5 text-sm rounded-md transition flex items-center gap-2 ${active ? "bg-cyan-600 text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
        >
            {icon}
            {label}
        </button>
    );
}

function Kpi({ label, value }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="text-2xl font-bold text-slate-800">{fmt(value)}</div>
        </div>
    );
}

function SortableTh({ label, right, active, dir, onClick }) {
    return (
        <th
            onClick={onClick}
            className={`px-3 py-2 font-medium cursor-pointer select-none hover:text-slate-700 ${right ? "text-right" : ""
                } ${active ? "text-cyan-700" : ""}`}
        >
            <span className={`inline-flex items-center gap-1 ${right ? "flex-row-reverse" : ""}`}>
                {label}
                {active ? (
                    dir === "asc" ? <FaSortUp className="text-xs" /> : <FaSortDown className="text-xs" />
                ) : (
                    <FaSort className="text-xs text-slate-300" />
                )}
            </span>
        </th>
    );
}

function Td({ children, right, mono, className = "" }) {
    return (
        <td
            className={`px-3 py-2 ${right ? "text-right tabular-nums" : ""} ${mono ? "font-mono text-xs" : ""
                } ${className}`}
        >
            {children}
        </td>
    );
}